import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IconPlus, IconTrash, IconUsersGroup } from '@tabler/icons-react';
import type { Group } from '../../../../entities/group';
import {
  createParticipant,
  deleteParticipant,
  fetchParticipants,
  updateParticipant,
  type LeaderBoardParticipant,
} from '../../../../entities/leaderBoardParticipants';
import { AddParticipantModal, DeleteParticipantModal } from '../../../../features/admin-lb-participants-form';
import { pb } from '../../../../shared/api/pocketbase';
import { logAction } from '../../../../shared/lib/userLogger';
import { Button } from '../../../../shared/ui/Button';

const LeaderBoardParticipantsSection: React.FC = () => {
  const [participants, setParticipants] = useState<LeaderBoardParticipant[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LeaderBoardParticipant | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetchParticipants()
      .then(setParticipants)
      .catch(() => setParticipants([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    pb.collection('groups')
      .getFullList<Group>({ sort: 'name', fields: 'id,name' })
      .then(setGroups)
      .catch(() => setGroups([]));
  }, [load]);

  const usedGroupIds = useMemo(() => new Set(participants.map(p => p.group)), [participants]);

  const groupOptions = useMemo(
    () => groups
      .filter(g => !usedGroupIds.has(g.id))
      .map(g => ({ value: g.id, label: g.name || g.id })),
    [groups, usedGroupIds],
  );

  const getGroupName = (p: LeaderBoardParticipant) =>
    p.expand?.group?.name || groups.find(g => g.id === p.group)?.name || p.group;

  const handleToggle = async (p: LeaderBoardParticipant) => {
    setTogglingId(p.id);
    try {
      const updated = await updateParticipant(p.id, { actual: !p.actual });
      setParticipants(prev => prev.map(x => x.id === p.id ? { ...x, actual: updated.actual } : x));
      logAction(`Toggled leaderboard group: ${getGroupName(p)} → ${updated.actual ? 'active' : 'inactive'}`, 'EDIT');
    } finally {
      setTogglingId(null);
    }
  };

  const handleAdd = async (groupId: string, actual: boolean) => {
    await createParticipant({ group: groupId, actual });
    logAction(`Added leaderboard participant: ${groups.find(g => g.id === groupId)?.name || groupId}`, 'ADD');
    load();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const name = getGroupName(deleteTarget);
    await deleteParticipant(deleteTarget.id);
    logAction(`Deleted leaderboard participant: ${name}`, 'REMOVE');
    load();
  };

  return (
    <div className="adm-section">
      <div className="adm-section-header adm-section-header-row">
        <div>
          <h2 className="adm-section-title">Участники лидер борда</h2>
          <p className="adm-section-sub">
            {loading ? 'Загрузка...' : `${participants.length} групп в рейтинге`}
          </p>
        </div>
        <Button variant="adm-add" type="button" onClick={() => setShowAdd(true)}>
          <IconPlus size={15} stroke={2.5} />Добавить группу
        </Button>
      </div>

      {loading ? (
        <div className="adm-loading">Загрузка...</div>
      ) : participants.length === 0 ? (
        <div className="adm-empty">Нет участников. Добавьте первую группу.</div>
      ) : (
        <div className="adm-lb-cards">
          {participants.map(p => (
            <div key={p.id} className={`adm-lb-card${p.actual ? ' adm-lb-card-active' : ''}`}>
              <div className="adm-lb-card-icon">
                <IconUsersGroup size={20} stroke={1.8} />
              </div>
              <div className="adm-lb-card-info">
                <div className="adm-lb-card-name">{getGroupName(p)}</div>
                <div className="adm-lb-card-meta">
                  {new Date(p.created).toLocaleDateString('ru-RU')}
                </div>
              </div>
              <button
                type="button"
                className={`adm-lb-actual-toggle${p.actual ? ' on' : ''}`}
                disabled={togglingId === p.id}
                onClick={() => handleToggle(p)}
              >
                {p.actual ? 'Активна' : 'Неактивна'}
              </button>
              <Button
                variant="adm-delete"
                type="button"
                title="Удалить"
                onClick={() => setDeleteTarget(p)}
              >
                <IconTrash size={14} stroke={2} />
              </Button>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <AddParticipantModal
          groupOptions={groupOptions}
          onClose={() => setShowAdd(false)}
          onSave={handleAdd}
        />
      )}

      {deleteTarget && (
        <DeleteParticipantModal
          groupName={getGroupName(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
};

export { LeaderBoardParticipantsSection };
